/**
 * VeyroCam — Product page
 *
 * Four independent behaviours, each guarded so a missing block never breaks
 * the others:
 *
 *   1. Gallery      slide switching + keyboard-navigable thumbnails
 *   2. Zoom         pointer-anchored magnification on the active slide
 *   3. Variants     option selection -> variant id, price, availability
 *   4. Sticky ATC   purchase bar shown once the main form scrolls away
 *
 * The page works without this file: options are real radio inputs inside a
 * form that posts to /cart/add, and the gallery shows its first image.
 */

(function () {
  'use strict';

  /* ======================================================================
     1 + 2. Gallery and zoom
     ====================================================================== */

  function initGallery(root) {
    var stage = root.querySelector('[data-gallery-stage]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));

    if (!stage || slides.length === 0) return;

    function show(mediaId) {
      slides.forEach(function (slide) {
        var active = slide.dataset.mediaId === String(mediaId);
        slide.classList.toggle('is-active', active);
        // aria-hidden on inactive slides keeps duplicate alt text out of the
        // accessibility tree — otherwise every image is announced at once.
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      thumbs.forEach(function (thumb) {
        thumb.setAttribute(
          'aria-current',
          thumb.dataset.mediaId === String(mediaId) ? 'true' : 'false'
        );
      });

      // Leaving the zoom engaged across a slide change strands the new image
      // at 2x with a stale origin.
      stage.classList.remove('is-zoomed');
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        show(thumb.dataset.mediaId);
      });

      // Roving arrow-key navigation, per the tablist pattern.
      thumb.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = thumbs[index + 1] || thumbs[0];
        if (event.key === 'ArrowLeft') next = thumbs[index - 1] || thumbs[thumbs.length - 1];
        if (!next) return;

        event.preventDefault();
        next.focus();
        show(next.dataset.mediaId);
      });
    });

    root.veyroShowMedia = show;

    /* ---- Zoom ---- */

    if (!stage.classList.contains('vc-gallery__stage--zoomable')) return;

    // Pointer-anchored zoom is meaningless on touch (no hover) and fights
    // with native pinch-zoom, so it is bound only for fine pointers.
    if (!window.matchMedia('(pointer: fine)').matches) return;

    stage.addEventListener('click', function () {
      stage.classList.toggle('is-zoomed');
    });

    stage.addEventListener('pointermove', function (event) {
      if (!stage.classList.contains('is-zoomed')) return;

      var rect = stage.getBoundingClientRect();
      var x = ((event.clientX - rect.left) / rect.width) * 100;
      var y = ((event.clientY - rect.top) / rect.height) * 100;

      stage.style.setProperty('--zoom-x', x + '%');
      stage.style.setProperty('--zoom-y', y + '%');
    });

    stage.addEventListener('pointerleave', function () {
      stage.classList.remove('is-zoomed');
    });
  }

  /* ======================================================================
     3. Variant selection
     ====================================================================== */

  function initVariants(form) {
    var payload = document.querySelector('[data-product-variants]');
    if (!payload) return;

    var variants;
    try {
      variants = JSON.parse(payload.textContent);
    } catch (error) {
      // A malformed payload must not take down add-to-cart: the form still
      // posts the server-rendered variant id.
      return;
    }

    if (!variants || !variants.length) return;

    var idField = form.querySelector('[data-variant-id]');
    var radios = Array.prototype.slice.call(
      form.querySelectorAll('[data-option-position]')
    );
    if (!idField || !radios.length) return;

    var priceEl = form.querySelector('.vc-price__current');
    var compareEl = form.querySelector('.vc-price__compare');
    var availabilityEl = form.querySelector('[data-availability]');
    var addButton = form.querySelector('[data-add-to-cart]');
    var addButtonText = form.querySelector('[data-add-to-cart-text]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var stickyAdd = document.querySelector('[data-sticky-add]');
    var gallery = document.querySelector('[data-vc-gallery]');

    function selectedOptions() {
      var values = [];
      radios.forEach(function (radio) {
        if (radio.checked) {
          values[parseInt(radio.dataset.optionPosition, 10) - 1] = radio.value;
        }
      });
      return values;
    }

    function findVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var candidate = variants[i];
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (candidate.options[j] !== options[j]) {
            match = false;
            break;
          }
        }
        if (match) return candidate;
      }
      return null;
    }

    function update() {
      var options = selectedOptions();
      var variant = findVariant(options);

      if (!variant) {
        // Combination does not exist. Disable purchase rather than silently
        // adding the wrong variant.
        idField.value = '';
        if (addButton) addButton.disabled = true;
        if (stickyAdd) stickyAdd.disabled = true;
        if (addButtonText) {
          addButtonText.textContent = addButtonText.dataset.unavailableText || '';
        }
        return;
      }

      idField.value = variant.id;
      idField.disabled = false;

      if (priceEl) priceEl.textContent = variant.price;
      if (stickyPrice) stickyPrice.textContent = variant.price;

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price !== variant.price) {
          compareEl.textContent = variant.compare_at_price;
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      [addButton, stickyAdd].forEach(function (button) {
        if (button) button.disabled = !variant.available;
      });

      if (addButtonText) {
        addButtonText.textContent = variant.available
          ? addButtonText.dataset.availableText
          : addButtonText.dataset.soldOutText;
      }

      if (availabilityEl) {
        availabilityEl.textContent = variant.available
          ? availabilityEl.dataset.inStockText
          : availabilityEl.dataset.soldOutText;
      }

      // Keep the URL in sync so a shared link reopens the same variant.
      if (window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (variant.featured_media_id && gallery && gallery.veyroShowMedia) {
        gallery.veyroShowMedia(variant.featured_media_id);
      }
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Reflect the choice in the option's summary label.
        var legend = radio.closest('fieldset');
        if (legend) {
          var readout = legend.querySelector('[data-option-value]');
          if (readout) readout.textContent = radio.value;
        }
        update();
      });
    });
  }

  /* ======================================================================
     4. Quantity stepper
     ====================================================================== */

  function initQuantity(root) {
    var input = root.querySelector('input[type="number"]');
    if (!input) return;

    function step(delta) {
      var min = parseInt(input.min, 10) || 1;
      var next = (parseInt(input.value, 10) || min) + delta;
      input.value = Math.max(min, next);
      // Dispatch so any listener (cart line items) reacts to the change.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var down = root.querySelector('[data-qty-down]');
    var up = root.querySelector('[data-qty-up]');
    if (down) down.addEventListener('click', function () { step(-1); });
    if (up) up.addEventListener('click', function () { step(1); });
  }

  /* ======================================================================
     5. Sticky add-to-cart
     ====================================================================== */

  function initStickyBar(form) {
    var bar = document.querySelector('[data-sticky-atc]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var anchor = form.querySelector('[data-add-to-cart]');
    if (!anchor) return;

    bar.hidden = false;

    // Show the bar only while the real button is off screen. Observing the
    // button itself means the bar never covers the control it duplicates.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -120px 0px', threshold: 0 }
    );

    observer.observe(anchor);
  }

  /* ====================================================================== */

  function init() {
    var gallery = document.querySelector('[data-vc-gallery]');
    if (gallery) initGallery(gallery);

    document.querySelectorAll('[data-vc-qty]').forEach(initQuantity);

    var form = document.getElementById('ProductForm');
    if (form) {
      initVariants(form);
      initStickyBar(form);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialise when the Theme Editor swaps the section out.
  document.addEventListener('shopify:section:load', init);
})();
